
import React, { useEffect } from "react";
import { usePopup } from "@/context/PopupContext";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import CouponPopup from "./CouponPopup";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

export const PopupManager: React.FC = () => {
  const { popups, hidePopup } = usePopup();

  // ESC key handler for non-persistent popups
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        const nonPersistentPopups = popups.filter(popup => !popup.persistent);
        if (nonPersistentPopups.length > 0) {
          hidePopup(nonPersistentPopups[nonPersistentPopups.length - 1].id);
        }
      }
    };

    window.addEventListener("keydown", handleEscKey);
    return () => window.removeEventListener("keydown", handleEscKey);
  }, [popups, hidePopup]);

  if (popups.length === 0) return null;

  return (
    <>
      {popups.map((popup) => (
        <Dialog key={popup.id} open={true} onOpenChange={() => !popup.persistent && hidePopup(popup.id)}>
          <DialogContent className={cn("p-0 overflow-hidden max-w-md border-none bg-transparent shadow-none", 
            popup.type === "custom" && "bg-background border shadow-lg"
          )}>
            {popup.type === "coupon" ? (
              <CouponPopup 
                title={popup.title} 
                message={popup.message} 
                onClose={() => hidePopup(popup.id)} 
              />
            ) : popup.type === "custom" && popup.component ? (
              <div className="relative">
                {!popup.persistent && (
                  <button 
                    onClick={() => hidePopup(popup.id)}
                    className="absolute right-4 top-4 z-10 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none"
                  >
                    <X className="h-4 w-4" />
                    <span className="sr-only">Close</span>
                  </button>
                )}
                {popup.component}
              </div>
            ) : (
              <div className="bg-white p-6 rounded-lg shadow-lg">
                {popup.title && <h2 className="text-xl font-semibold mb-2">{popup.title}</h2>}
                {popup.message && <p className="text-gray-700">{popup.message}</p>}
                <button
                  onClick={() => hidePopup(popup.id)}
                  className="mt-4 px-4 py-2 bg-primary text-white rounded hover:bg-primary/90"
                >
                  Close
                </button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      ))}
    </>
  );
};
